import { useForm } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import { useUpdateProfileMutation } from '@unsw/api/mutations/auth'
import { useAuthStore } from '@unsw/store/auth'
import { Button } from '@unsw/ui/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@unsw/ui/components/ui/card'
import { Input } from '@unsw/ui/components/ui/input'
import { Label } from '@unsw/ui/components/ui/label'
import { toast } from '@unsw/ui/components/ui/sonner'
import { Loader2 } from 'lucide-react'

export const Route = createFileRoute('/dashboard/profile/personal-information')({
  component: PersonalInformationPage,
})

function PersonalInformationPage() {
  const { user, setUser } = useAuthStore()
  const updateProfileMutation = useUpdateProfileMutation()

  const form = useForm({
    defaultValues: {
      full_name: user?.full_name || '',
    },
    onSubmit: async ({ value }) => {
      try {
        await updateProfileMutation.mutateAsync({
          full_name: value.full_name,
        })
        // Update local user state with new name
        if (user) {
          setUser({ ...user, full_name: value.full_name })
        }
        toast.success('Profile updated successfully')
      } catch {
        toast.error('Failed to update profile')
      }
    },
  })

  const isLoading = updateProfileMutation.isPending

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personal Information</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-6"
        >
          {/* Username (read-only) */}
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input id="username" value={user?.username || ''} disabled className="bg-muted" />
          </div>

          {/* Full Name (editable) */}
          <form.Field
            name="full_name"
            validators={{
              onChange: ({ value }) => (!value ? 'Alias is required' : undefined),
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="full_name">Alias</Label>
                <Input
                  id="full_name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Enter your alias"
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">{field.state.meta.errors.join(', ')}</p>
                )}
              </div>
            )}
          </form.Field>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={() => form.reset()} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Profile
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
